import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useNavigate } from 'react-router-dom';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction,
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { colorParser } from 'src/components/paginated-data-grid/ColorParser';
import UnitDialog from '../products/ProductDialog';
import ProductImportDialog from '../products/ProductImportDialog';

interface Unit {
  id: string;
  itemId: string;
  itemName: string;
  barcode: string;
  symbol: string;
  sourceRef: string;
  unitOfMeasurement: string;
  isSynced: boolean;
  createdAt: string;
  updatedAt: string;
  onHandQuantity: number;
  expectedQuantity: number;
  itemAdditionalProperties: Record<string, any>;
  itemMinNumber?: number | null;
  itemMaxNumber?: number | null;
}

const LS_KEY = 'units-dynamic-prop-keys';

const UnknownUnitsListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [switchBarcodeDialogOpen, setSwitchBarcodeDialogOpen] = useState(false);
  const [selectedUnitForSwitch, setSelectedUnitForSwitch] = useState<Unit | null>(null);
  const [targetBarcode, setTargetBarcode] = useState('');
  const [searchingBarcode, setSearchingBarcode] = useState(false);
  const [barcodeSearchResult, setBarcodeSearchResult] = useState<{exists: boolean, unitData?: any} | null>(null);
  const [switchingBarcode, setSwitchingBarcode] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // ---------- Resolve Unknowns ----------
  const handleResolveUnknowns = () => {
    setResolveDialogOpen(true);
  };

  const handleResolveSuccess = () => {
    setResolveDialogOpen(false);
    setRefreshKey((p) => p + 1);
    dispatch(showAlert({ message: 'Unknown units resolved successfully!', type: 'success' }));
  };

  const formatPropertyName = useCallback((key: string): string => {
    const formatted = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    const specialCases: Record<string, string> = {
      COLOR: 'Color',
      Color: 'Color',
      Brand: 'Brand',
      MainCategory: 'Main Category',
      Category: 'Category',
      Generic: 'Generic Code',
      size: 'Size',
      gender: 'Gender',
      season: 'Season',
    };
    return specialCases[key] || formatted;
  }, []);

  const initialSeenKeys = (() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  })();

  const seenDynamicKeysRef = useRef<Set<string>>(new Set(initialSeenKeys));

  const persistSeenKeys = (set: Set<string>) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Array.from(set)));
    } catch {}
  };

  const buildColumns = useCallback(
    (dynamicKeys: string[]): PaginatedDataGridColumn[] => {
      const base: PaginatedDataGridColumn[] = [
        {
          field: 'barcode',
          headerName: 'Barcode',
          flex: 1,
          minWidth: 130,
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              color="primary"
              sx={{ cursor: 'pointer', fontWeight: 'medium' }}
              onClick={() => navigate(`/units/${params.row.id}`)}
            >
              {params.value}
            </Typography>
          ),
        },
        { field: 'itemName', headerName: 'Name', flex: 1.5, minWidth: 160 },
        { field: 'symbol', headerName: 'Symbol', flex: 1, minWidth: 120 },
        { field: 'sourceRef', headerName: 'SourceRef', flex: 1, minWidth: 120 },
        { field: 'unitOfMeasurement', headerName: 'Measurement', flex: 1, minWidth: 120 },
        { field: 'expectedQuantity', headerName: 'Expected', width: 110, type: 'number' },
        { field: 'onHandQuantity', headerName: 'On Hand', width: 110, type: 'number' },
        {
          field: 'isSynced',
          headerName: 'Synced',
          width: 100,
          renderCell: (params: GridRenderCellParams) => (
            <Chip
              label={params.value ? 'Yes' : 'No'}
              color={params.value ? 'success' : 'warning'}
              size="small"
              variant="outlined"
            />
          ),
        },
      ];

      const dynamicCols: PaginatedDataGridColumn[] = dynamicKeys.sort().map((key) => {
        const col: PaginatedDataGridColumn = {
          field: key,
          headerName: formatPropertyName(key),
          flex: 1,
          minWidth: 110,
          valueGetter: (params) => params.row.itemAdditionalProperties?.[key] ?? null,
          sortable: true,
        };

        // Color swatch
        if (key.toLowerCase().includes('color')) {
          col.columnType = 'color';
          col.renderCell = (params: GridRenderCellParams) => {
            if (!params.value) return <Typography color="text.secondary">-</Typography>;
            const colorValue = colorParser(params.value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: colorValue,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                />
                <Typography variant="body2">{params.value}</Typography>
              </Box>
            );
          };
        }

        // Category/Brand/Season as chips
        if (
          ['category', 'maincategory', 'brand', 'season'].some((f) =>
            key.toLowerCase().includes(f)
          )
        ) {
          col.renderCell = (params: GridRenderCellParams) => {
            if (!params.value) return <Typography color="text.secondary">-</Typography>;
            return <Chip label={params.value} size="small" variant="outlined" color="primary" />;
          };
        }

        return col;
      });

      return [...base];
    },
    [formatPropertyName, navigate]
  );

  const [dynamicColumns, setDynamicColumns] = useState<PaginatedDataGridColumn[]>(
    buildColumns(Array.from(seenDynamicKeysRef.current))
  );

  const fetchUnits = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      const queryParams = new URLSearchParams({
        PageNumber: String(params.page + 1),
        PageSize: String(params.pageSize),
      });

      if (params.search) queryParams.append('SearchTerm', params.search);

      if (params.sortModel?.length > 0) {
        const { field, sort } = params.sortModel[0];
        const isDynamic = seenDynamicKeysRef.current.has(field);
        queryParams.append('SortBy', isDynamic ? `itemAdditionalProperties.${field}` : field);
        queryParams.append('SortDirection', sort || 'asc');
      }

      const res = await DataService.get(`api/Unit/unsynced?${queryParams.toString()}`, {});
      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to fetch units`);
      const json = await res.json();

      let items: Unit[] = [];
      if (json?.data?.items) {
        if (Array.isArray(json.data.items)) items = json.data.items;
        else if (typeof json.data.items === 'object') items = Object.values(json.data.items);
      } else if (Array.isArray(json?.items)) {
        items = json.items;
      }

      const newKeys: string[] = [];
      for (const u of items) {
        if (u.itemAdditionalProperties) {
          Object.keys(u.itemAdditionalProperties).forEach((k) => {
            if (!seenDynamicKeysRef.current.has(k)) {
              seenDynamicKeysRef.current.add(k);
              newKeys.push(k);
            }
          });
        }
      }

      if (newKeys.length > 0) {
        persistSeenKeys(seenDynamicKeysRef.current);
        setDynamicColumns(buildColumns(Array.from(seenDynamicKeysRef.current)));
      }

      return {
        items,
        totalCount: json?.data?.totalCount ?? json?.totalCount ?? items.length,
        pageNumber: json?.data?.pageNumber ?? json?.pageNumber ?? params.page + 1,
        pageSize: json?.data?.pageSize ?? json?.pageSize ?? params.pageSize,
        totalPages:
          json?.data?.totalPages ??
          json?.totalPages ??
          Math.ceil(
            (json?.data?.totalCount ?? json?.totalCount ?? items.length) / params.pageSize
          ),
        hasNext: json?.data?.hasNext ?? json?.hasNext ?? false,
        hasPrevious: json?.data?.hasPrevious ?? json?.hasPrevious ?? false,
      };
    },
    [buildColumns]
  );

  const actions: PaginatedDataGridAction[] = [
    {
      key: 'resolve',
      title: 'Resolve Unknowns',
      icon: <BuildIcon />,
      onClick: handleResolveUnknowns,
      disabled: () => false,
      color: 'warning',
    },
  ];

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await DataService.delete(`api/Unit/delete/${deleteId}`, {});
      if (res.ok) {
        dispatch(showAlert({ message: 'Unit deleted successfully!', type: 'success' }));
        setRefreshKey((p) => p + 1);
      } else {
        dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
    }
    setDeleteId(null);
  };

  const handleRowClick = (params: any) => navigate(`/Units/${params.row.id}`);

  const handleEditUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = (updatedUnit: any) => {
    dispatch(showAlert({ message: 'Unit updated successfully!', type: 'success' }));
    setRefreshKey((p) => p + 1);
    setEditDialogOpen(false);
    setSelectedUnit(null);
  };

  // ---------- Switch Barcode ----------
  const handleSwitchBarcode = (unit: Unit) => {
    setSelectedUnitForSwitch(unit);
    setTargetBarcode('');
    setBarcodeSearchResult(null);
    setSwitchBarcodeDialogOpen(true);
  };

  const searchTargetBarcode = async (barcode: string) => {
    if (!barcode.trim()) {
      setBarcodeSearchResult(null);
      return;
    }

    setSearchingBarcode(true);
    try {
      const response = await DataService.get(`api/inventoryItem/search-barcode/${encodeURIComponent(barcode)}`);

      if (!response.ok) {
        setBarcodeSearchResult({ exists: false });
        return;
      }

      const result = await response.json();

      // Check if barcode exists AND is not of type "unknown" and status is not "not_found"
      if (result?.success && result?.data) {
        const isValidBarcode =
          result.data.type !== 'unknown' &&
          result.data.status !== 'not_found' &&
          result.data.unitId;

        setBarcodeSearchResult({
          exists: isValidBarcode,
          unitData: isValidBarcode ? result.data : null
        });
      } else {
        setBarcodeSearchResult({ exists: false });
      }
    } catch (error) {
      console.error('Error searching barcode:', error);
      setBarcodeSearchResult({ exists: false });
    } finally {
      setSearchingBarcode(false);
    }
  };

  const handleBarcodeKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      searchTargetBarcode(targetBarcode);
    }
  };

  const handleConfirmSwitch = async () => {
    if (!selectedUnitForSwitch || !targetBarcode.trim() || !barcodeSearchResult?.exists) {
      return;
    }

    setSwitchingBarcode(true);
    try {
      const payload = {
        sourceBarcode: selectedUnitForSwitch.barcode,
        targetBarcode: targetBarcode.trim()
      };

      const response = await DataService.post('api/Unit/merge-barcode', payload);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to switch barcode');
      }

      dispatch(showAlert({
        message: `Successfully switched barcode from ${selectedUnitForSwitch.barcode} to ${targetBarcode}`,
        type: 'success'
      }));

      setRefreshKey((p) => p + 1);
      setSwitchBarcodeDialogOpen(false);
      setSelectedUnitForSwitch(null);
      setTargetBarcode('');
      setBarcodeSearchResult(null);
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to switch barcode',
        type: 'error'
      }));
    } finally {
      setSwitchingBarcode(false);
    }
  };

  const handleCloseSwitchDialog = () => {
    if (!switchingBarcode) {
      setSwitchBarcodeDialogOpen(false);
      setSelectedUnitForSwitch(null);
      setTargetBarcode('');
      setBarcodeSearchResult(null);
    }
  };

  return (
    <>
      <PaginatedDataGrid
        key={refreshKey}
        title="Unknown Units"
        columns={dynamicColumns}
        idField="id"
        useBackendPagination
        onDataFetch={fetchUnits}
        cacheTimeout={300000}
        actions={actions}
        wmsMode
        densityLevel="standard"
        autoHeight={true}
        compactEmptyState={true}
        responsiveColumns={true}
        hideFooterWhenEmpty={true}
        striped
        maxHeight={700}
        onRowClick={handleRowClick}
        rowActions={[
          {
            key: 'switch-barcode',
            icon: <SwapHorizIcon />,
            label: 'Switch Barcode',
            color: 'warning',
            onClick: (_id: GridRowId, row: any) => handleSwitchBarcode(row),
          },
          {
            key: 'edit',
            icon: <EditIcon />,
            label: 'Edit Unit',
            color: 'primary',
            onClick: (_id: GridRowId, row: any) => handleEditUnit(row),
          },
          // {
          //   key: 'delete',
          //   icon: <DeleteIcon />,
          //   label: 'Delete',
          //   color: 'error',
          //   onClick: (_id: GridRowId, row: any) => setDeleteId(String(row.id)),
          // },
        ]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
      />

      <SimpleConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Unit"
        message="Are you sure you want to delete this Unit? This action cannot be undone."
      />

      <UnitDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUnit(null);
        }}
        unit={selectedUnit ? {
          ...selectedUnit,
          name: selectedUnit.itemName,
          additionalProperties: selectedUnit.itemAdditionalProperties,
          minNumber: selectedUnit.itemMinNumber,
          maxNumber: selectedUnit.itemMaxNumber,
          hasProperties: !!selectedUnit.itemAdditionalProperties && Object.keys(selectedUnit.itemAdditionalProperties).length > 0,
          propertyCount: selectedUnit.itemAdditionalProperties ? Object.keys(selectedUnit.itemAdditionalProperties).length : 0
        } : null}
        mode="edit"
        onSuccess={handleEditSuccess}
      />

      <ProductImportDialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        onSuccess={handleResolveSuccess}
      />

      <Dialog
        open={switchBarcodeDialogOpen}
        onClose={handleCloseSwitchDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SwapHorizIcon color="warning" />
            <Typography variant="h6">Switch Barcode</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {selectedUnitForSwitch && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Current Barcode (Unsynced):
                </Typography>
                <Chip
                  label={selectedUnitForSwitch.barcode}
                  color="warning"
                  variant="outlined"
                  size="medium"
                  sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Item: {selectedUnitForSwitch.itemName}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Scan New Barcode:
              </Typography>
              <TextField
                fullWidth
                value={targetBarcode}
                onChange={(e) => {
                  setTargetBarcode(e.target.value);
                  setBarcodeSearchResult(null);
                }}
                onKeyPress={handleBarcodeKeyPress}
                onBlur={() => searchTargetBarcode(targetBarcode)}
                placeholder="Scan or enter target barcode"
                autoFocus
                disabled={switchingBarcode}
                InputProps={{
                  startAdornment: <QrCodeScannerIcon sx={{ mr: 1, color: 'action.active' }} />,
                  endAdornment: searchingBarcode && <CircularProgress size={20} />
                }}
              />
            </Box>

            {barcodeSearchResult && (
              <Box>
                {barcodeSearchResult.exists ? (
                  <Alert severity="success" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Barcode found in system!
                    </Typography>
                    {barcodeSearchResult.unitData && (
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2">
                          <strong>Item:</strong> {barcodeSearchResult.unitData.itemName}
                        </Typography>
                        {barcodeSearchResult.unitData.symbol && (
                          <Typography variant="body2">
                            <strong>Symbol:</strong> {barcodeSearchResult.unitData.symbol}
                          </Typography>
                        )}
                        {barcodeSearchResult.unitData.unitOfMeasurement && (
                          <Typography variant="body2">
                            <strong>UOM:</strong> {barcodeSearchResult.unitData.unitOfMeasurement}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontStyle: 'italic' }}>
                      Do you want to merge this unsynced unit with the existing barcode?
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <Typography variant="body2">
                      Barcode "{targetBarcode}" not found in the system. Please scan an existing barcode.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {!barcodeSearchResult && targetBarcode.trim() && !searchingBarcode && (
              <Alert severity="info">
                <Typography variant="body2">
                  Press Enter or click outside the field to search for this barcode.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseSwitchDialog}
            disabled={switchingBarcode}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSwitch}
            variant="contained"
            color="warning"
            disabled={!barcodeSearchResult?.exists || switchingBarcode}
            startIcon={switchingBarcode ? <CircularProgress size={20} /> : <SwapHorizIcon />}
          >
            {switchingBarcode ? 'Switching...' : 'Confirm Switch'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnknownUnitsListing;
